import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { Badge } from "@/components/ui/badge";
import { 
  Calendar, 
  Clock, 
  Settings, 
  BarChart3,
  AlertCircle,
  Trash2,
  CalendarDays,
  CalendarClock,
  Info,
  ChevronRight,
  Users
} from "lucide-react";
import { format, parse, differenceInDays, differenceInWeeks, addWeeks, startOfWeek, isMonday, isValid } from "date-fns";
import { Season } from "@/types/classTypes";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { countClassesInSeason } from "@/services/seasonService";

const DATE_FORMAT = "MM/dd/yyyy";

interface SeasonFormData {
  name: string;
  type: string;
  start_date: string;
  end_date: string;
  registration_start: string;
  registration_end: string;
  registration_fee_enabled: boolean;
  tuition_prepay_enabled: boolean;
  is_active: boolean;
}

interface SeasonFormFieldsProps {
  formData: SeasonFormData;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSwitchChange?: (name: string, checked: boolean) => void;
  handleDateChange?: (name: string, date: Date | undefined) => void;
  onDelete?: (season: Season) => void;
  season: Season;
}

// Duration presets for real-world activity centers
const durationPresets = [
  { weeks: 6, label: "6-Week Session", description: "Popular for skill-based programs" },
  { weeks: 8, label: "8-Week Session", description: "Standard session length" },
  { weeks: 10, label: "10-Week Session", description: "Extended session" },
  { weeks: 12, label: "12-Week Session", description: "Full quarter program" },
];

const SeasonFormFields: React.FC<SeasonFormFieldsProps> = ({
  formData,
  handleChange,
  handleSwitchChange,
  handleDateChange,
  onDelete,
  season,
}) => {
  const [classCount, setClassCount] = useState<number>(0);
  const [loadingStats, setLoadingStats] = useState(true);

  // Fetch class count for statistics
  useEffect(() => {
    const fetchClassCount = async () => {
      try {
        const count = await countClassesInSeason(season.id);
        setClassCount(count);
      } catch (error) {
        console.error("Error fetching class count:", error);
      } finally {
        setLoadingStats(false);
      }
    };
    fetchClassCount();
  }, [season.id]);

  const onSwitchChange = (name: string, checked: boolean) => {
    if (handleSwitchChange) {
      handleSwitchChange(name, checked);
    }
  };

  const onDateChange = (name: string, date: Date | undefined) => {
    if (handleDateChange) {
      handleDateChange(name, date);
    }
  };

  // Calculate session duration
  const calculateDuration = () => {
    try {
      const start = parse(formData.start_date, DATE_FORMAT, new Date());
      const end = parse(formData.end_date, DATE_FORMAT, new Date());
      const days = differenceInDays(end, start);
      const weeks = Math.ceil(days / 7);
      return { days, weeks };
    } catch {
      return { days: 0, weeks: 0 };
    }
  };

  // Calculate registration window
  const calculateRegistrationWindow = () => {
    try {
      const regStart = parse(formData.registration_start, DATE_FORMAT, new Date());
      const regEnd = parse(formData.registration_end, DATE_FORMAT, new Date());
      const days = differenceInDays(regEnd, regStart);
      return days;
    } catch {
      return 0;
    }
  };

  // Apply duration preset
  const applyDurationPreset = (weeks: number) => {
    const nextMonday = startOfWeek(new Date(), { weekStartsOn: 1 });
    const startDate = isMonday(new Date()) ? new Date() : nextMonday;
    const endDate = addWeeks(startDate, weeks);
    
    // Set registration to open 4 weeks before start
    const regStart = addWeeks(startDate, -4);
    const regEnd = addWeeks(startDate, -1);
    
    onDateChange("start_date", startDate);
    onDateChange("end_date", endDate);
    onDateChange("registration_start", regStart);
    onDateChange("registration_end", regEnd);
  };

  const duration = calculateDuration();
  const registrationWindow = calculateRegistrationWindow();

  // Enhanced date input component
  const DateInput = ({ 
    name, 
    label, 
    value,
    icon: Icon
  }: { 
    name: string;
    label: string;
    value: string;
    icon: React.ElementType;
  }) => {
    let dateValue: Date | undefined;
    
    if (value) {
      try {
        // Try parsing with the expected format first
        dateValue = parse(value, DATE_FORMAT, new Date());
        
        // If parsing results in an invalid date, try treating it as ISO string
        if (!isValid(dateValue)) {
          dateValue = new Date(value);
          
          // If still invalid, set to undefined
          if (!isValid(dateValue)) {
            console.warn(`Invalid date value for ${name}:`, value);
            dateValue = undefined;
          }
        }
      } catch (error) {
        console.warn(`Error parsing date for ${name}:`, value, error);
        dateValue = undefined;
      }
    }
    
    return (
      <div className="space-y-2">
        <Label htmlFor={name} className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {label}
        </Label>
        <div className="flex gap-2">
          <DatePicker
            value={dateValue}
            onChange={(date) => onDateChange(name, date)}
            disabled={false}
          />
          <Input
            id={name}
            name={name}
            value={value}
            onChange={handleChange}
            placeholder="MM/DD/YYYY"
            className="flex-1"
          />
        </div>
      </div>
    );
  };

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete(season);
    }
  };

  return (
    <div className="space-y-8">
      {/* Basic Information */}
      <Card className="border border-orange-200 bg-orange-50/30">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
              <Calendar className="h-4 w-4 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-orange-900">Basic Information</h3>
          </div>
          
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Season Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Spring Session 2025"
                required
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Session Dates */}
      <Card className="border border-blue-200 bg-blue-50/30">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <CalendarDays className="h-4 w-4 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-blue-900">Session Dates</h3>
          </div>
          
          {/* Duration Presets */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-blue-800 mb-3">Quick Duration Presets</h4>
            <div className="grid grid-cols-2 gap-3">
              {durationPresets.map((preset) => (
                <Button
                  key={preset.weeks}
                  type="button"
                  variant="outline"
                  className="h-auto py-3 px-4 justify-start text-left border-blue-200 hover:bg-blue-100"
                  onClick={() => applyDurationPreset(preset.weeks)}
                >
                  <div className="w-full">
                    <div className="font-medium text-sm">{preset.label}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {preset.description}
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <DateInput
              name="start_date"
              label="Session Start Date"
              value={formData.start_date}
              icon={CalendarClock}
            />
            
            <DateInput
              name="end_date"
              label="Session End Date"
              value={formData.end_date}
              icon={CalendarClock}
            />
          </div>
          
          {duration.weeks > 0 && (
            <div className="mt-4 p-3 bg-blue-100 rounded-lg">
              <div className="text-sm text-blue-800">
                <strong>Session Duration:</strong> {duration.weeks} weeks ({duration.days} days)
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Registration Window */}
      <Card className="border border-green-200 bg-green-50/30">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <Clock className="h-4 w-4 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-green-900">Registration Window</h3>
          </div>
          
          <div className="mb-4 p-3 bg-green-100 rounded-lg flex items-start gap-2">
            <Info className="h-4 w-4 text-green-700 mt-0.5" />
            <p className="text-sm text-green-800">
              Set when families can register for classes in this session. Registration typically opens 4-6 weeks before the session starts.
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <DateInput
              name="registration_start"
              label="Registration Opens"
              value={formData.registration_start}
              icon={CalendarClock}
            />
            
            <DateInput
              name="registration_end"
              label="Registration Closes"
              value={formData.registration_end}
              icon={CalendarClock}
            />
          </div>
          
          {registrationWindow > 0 && (
            <div className="mt-4 p-3 bg-green-100 rounded-lg">
              <div className="text-sm text-green-800">
                <strong>Registration Period:</strong> {registrationWindow} days
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Session Settings */}
      <Card className="border border-purple-200 bg-purple-50/30">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <Settings className="h-4 w-4 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-purple-900">Session Settings</h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between space-x-2 border border-purple-200 p-3 rounded-md bg-purple-50">
              <div>
                <Label htmlFor="registration_fee_enabled" className="text-base">Registration Fee</Label>
                <p className="text-sm text-muted-foreground">Charge a one-time registration fee for this session</p>
              </div>
              <Switch
                id="registration_fee_enabled"
                checked={formData.registration_fee_enabled}
                onCheckedChange={(checked) => onSwitchChange('registration_fee_enabled', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between space-x-2 border border-purple-200 p-3 rounded-md bg-purple-50">
              <div>
                <Label htmlFor="tuition_prepay_enabled" className="text-base">Tuition Prepay</Label>
                <p className="text-sm text-muted-foreground">Collect first tuition payment during registration</p>
              </div>
              <Switch
                id="tuition_prepay_enabled"
                checked={formData.tuition_prepay_enabled}
                onCheckedChange={(checked) => onSwitchChange('tuition_prepay_enabled', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between space-x-2 border border-purple-200 p-3 rounded-md bg-purple-50">
              <div>
                <Label htmlFor="is_active" className="text-base">Active Session</Label>
                <p className="text-sm text-muted-foreground">
                  Mark as currently active (multiple active sessions are normal)
                </p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => onSwitchChange('is_active', checked)}
              />
            </div>
            
            {formData.is_active && (
              <div className="flex items-center gap-2 p-3 bg-purple-100 rounded-lg">
                <Info className="h-4 w-4 text-purple-700" />
                <p className="text-sm text-purple-800">
                  This session is marked as active. You can have multiple active sessions for different programs.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Statistics & Overview */}
      <Card className="border border-gray-200 bg-gray-50/30">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
              <BarChart3 className="h-4 w-4 text-gray-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Statistics & Overview</h3>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Users className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-600">Classes</span>
              </div>
              <div className="text-2xl font-semibold text-gray-900">
                {loadingStats ? "..." : classCount}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {classCount === 1 ? "class" : "classes"} in this session
              </p>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-600">Duration</span>
              </div>
              <div className="text-2xl font-semibold text-gray-900">
                {duration.weeks}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                weeks ({duration.days} days)
              </p>
            </div>
          </div>
          
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Created:</span>
              <span className="text-gray-900">{format(new Date(season.created_at), "MMM d, yyyy")}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Registration Window:</span>
              <span className="text-gray-900">{registrationWindow} days</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Status:</span>
              <Badge variant={formData.is_active ? "success" : "muted"}>
                {formData.is_active ? "Active" : "Inactive"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      {onDelete && (
        <Card className="border border-destructive/20 bg-destructive/10">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-destructive/20 rounded-full flex items-center justify-center">
                <AlertCircle className="h-4 w-4 text-destructive" />
              </div>
              <h3 className="text-lg font-semibold text-destructive">Danger Zone</h3>
            </div>
            
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
              <h4 className="text-destructive font-medium text-sm mb-2">Delete this season</h4>
              <p className="text-sm text-destructive mb-4">
                Deleting a season will remove it permanently. Classes in this season will need to be reassigned.
              </p>
              
              {classCount > 0 && (
                <div className="mb-4 p-3 bg-destructive/10 rounded border border-destructive/20">
                  <p className="text-sm text-destructive font-medium">
                    ⚠️ This season has {classCount} {classCount === 1 ? "class" : "classes"} that will need to be reassigned.
                  </p>
                </div>
              )}
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" className="gap-1.5">
                    <Trash2 className="h-4 w-4" />
                    Delete Season
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Season</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete "{season.name}"? 
                      {classCount > 0 && (
                        <span className="block mt-2 font-medium">
                          This season has {classCount} {classCount === 1 ? "class" : "classes"} that will need to be reassigned.
                        </span>
                      )}
                      This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      className="bg-destructive hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SeasonFormFields;